import React, { useState } from 'react';
import { ThumbsUp, Minus, HelpCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ResponseButtons = ({ 
  userResponse, 
  onResponseChange, 
  disabled = false,
  featureReference  
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleResponseClick = async (value) => {
    if (disabled || isLoading) return;
    
    const isDeselecting = userResponse?.response_value === value;
    const newValue = isDeselecting ? null : value;
    
    setIsLoading(true);
    try {
      await onResponseChange(featureReference, newValue);
    } catch (error) {
      console.error('Error updating response:', error);
      toast.error('Failed to update response. Please try again.');
    } finally {
      // Always clear loading state
      setIsLoading(false);
    }
  };

  const getButtonStyles = (value) => {
    const baseClasses = 'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors';
    const disabledClasses = (disabled || isLoading) ? '!opacity-50 !cursor-not-allowed' : '';
    const selected = userResponse?.response_value === value;
    
    let colorClasses = '';
    if (selected) {
      switch (value) {
        case 'required':
          colorClasses = 'bg-green-600 text-white';
          break;
        case 'nice_to_have':
          colorClasses = 'bg-blue-600 text-white';
          break;
        case 'not_needed':
          colorClasses = 'bg-gray-600 text-white';
          break;
        case 'dont_know':
          colorClasses = 'bg-amber-500 text-white';
          break;
      }
    } else {
      switch (value) {
        case 'required':
          colorClasses = 'border-2 border-green-600 text-green-700 hover:bg-green-50';
          break;
        case 'nice_to_have':
          colorClasses = 'border-2 border-blue-600 text-blue-700 hover:bg-blue-50';
          break;
        case 'not_needed':
          colorClasses = 'border-2 border-gray-600 text-gray-700 hover:bg-gray-50';
          break;
        case 'dont_know':
          colorClasses = 'border-2 border-amber-500 text-amber-700 hover:bg-amber-50';
          break;
      }
    }

    return `${baseClasses} ${disabledClasses} ${colorClasses}`;
  };

  const buttons = [
    { value: 'required', label: 'Required', icon: <ThumbsUp className="w-4 h-4" /> },
    { value: 'nice_to_have', label: 'Nice to Have', icon: <ThumbsUp className="w-4 h-4" /> },
    { value: 'not_needed', label: 'Not Needed', icon: <Minus className="w-4 h-4" /> },
    { value: 'dont_know', label: "Don't Know", icon: <HelpCircle className="w-4 h-4" /> }
  ];

  return (
    <div className="flex gap-3 flex-wrap">
      {buttons.map(({ value, label, icon }) => {
        const isCurrentValue = userResponse?.response_value === value;
        return (
          <button
            key={value}
            onClick={() => handleResponseClick(value)}
            disabled={disabled || isLoading}
            className={getButtonStyles(value)}
          >
            <div className="flex items-center gap-2">
              {isLoading && isCurrentValue ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                icon
              )}
              <span>{label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ResponseButtons;